// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * bot.ts — a heuristic player. Used by the balance sim (which was written
 * BEFORE any tuning, so it could referee the design) and by the solo "race the
 * crew" opponents.
 *
 * The base heuristic is the well-known Dellacherie-style linear evaluation
 * (aggregate height / holes / bumpiness / lines). The term that matters here is
 * `trim`: without it the bot plays a generic stacker and is blind to this
 * game's actual rule, which makes it useless as a referee. Keeping `trim` as a
 * WEIGHT rather than baking it in gives the sim a real control arm — a
 * trim-blind bot is the "does the signature mechanic even matter?" experiment
 * that sporeline learned to run the hard way.
 */

import { EMPTY, cellsAt, massOf } from './pieces';
import type { Game } from './game';

export interface BotWeights {
  height: number;
  holes: number;
  bumps: number;
  lines: number;
  /** Penalty on |list| after the placement. 0 = plays this game blind. */
  trim: number;
  /** Penalty on how deep the deepest single-column well is. */
  wells: number;
}

export const BOT_PRESETS: Record<string, BotWeights> = {
  /** Plays the game as designed: cares about the list. */
  sailor: { height: 0.52, holes: 3.6, bumps: 0.22, lines: 1.1, trim: 3.0, wells: 0.28 },
  /** Control arm: a competent generic stacker that cannot see the list at all. */
  landlubber: { height: 0.52, holes: 3.6, bumps: 0.22, lines: 1.1, trim: 0, wells: 0.28 },
  /** Greedy: chases clears, ignores structure. Should lose. */
  greedy: { height: 0.15, holes: 1.2, bumps: 0.05, lines: 3.0, trim: 0.5, wells: 0.05 },
  /** Over-corrects: obsessed with trim to the exclusion of shape. Should lose. */
  ballast: { height: 0.3, holes: 2.0, bumps: 0.15, lines: 0.6, trim: 12.0, wells: 0.15 },
};

interface Metrics {
  aggHeight: number;
  holes: number;
  bumps: number;
  cleared: number;
  list: number;
  deepestWell: number;
}

/**
 * Metrics for the board that WOULD result from this landing. Works on a scratch
 * copy; the real board is never touched.
 */
function evaluateLanding(g: Game, type: number, rot: number, x: number, y: number): Metrics {
  const { cols, rows } = g;
  const board = Uint8Array.from(g.cells);
  for (const [c, r] of cellsAt(type, rot, x, y)) {
    if (c < 0 || c >= cols || r < 0 || r >= rows) continue;
    board[r * cols + c] = type;
  }

  // Remove full rows, exactly as the game would.
  const keep: number[] = [];
  let cleared = 0;
  for (let r = 0; r < rows; r++) {
    let solid = true;
    for (let c = 0; c < cols; c++) if (board[r * cols + c] === EMPTY) { solid = false; break; }
    if (solid) cleared++; else keep.push(r);
  }
  const settled = new Uint8Array(cols * rows);
  let write = rows - 1;
  for (let i = keep.length - 1; i >= 0; i--) {
    for (let c = 0; c < cols; c++) settled[write * cols + c] = board[keep[i] * cols + c];
    write--;
  }

  const heights = new Array<number>(cols).fill(0);
  let holes = 0;
  for (let c = 0; c < cols; c++) {
    let top = -1;
    for (let r = 0; r < rows; r++) {
      if (settled[r * cols + c] !== EMPTY) { top = r; break; }
    }
    if (top < 0) { heights[c] = 0; continue; }
    heights[c] = rows - top;
    for (let r = top + 1; r < rows; r++) if (settled[r * cols + c] === EMPTY) holes++;
  }

  let aggHeight = 0;
  let bumps = 0;
  for (let c = 0; c < cols; c++) {
    aggHeight += heights[c];
    if (c > 0) bumps += Math.abs(heights[c] - heights[c - 1]);
  }

  let deepestWell = 0;
  for (let c = 0; c < cols; c++) {
    const left = c === 0 ? rows : heights[c - 1];
    const right = c === cols - 1 ? rows : heights[c + 1];
    deepestWell = Math.max(deepestWell, Math.min(left, right) - heights[c]);
  }

  const centre = (cols - 1) / 2;
  let torque = 0;
  let maxT = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const v = settled[r * cols + c];
      if (v === EMPTY) continue;
      const m = massOf(v);
      torque += m * (c - centre);
      maxT += m * Math.abs(c - centre);
    }
  }
  const list = Math.max(-1, Math.min(1, torque / (maxT + g.mode.keel)));

  return { aggHeight, holes, bumps, cleared, list, deepestWell };
}

function scoreMetrics(m: Metrics, w: BotWeights): number {
  return (
    -w.height * m.aggHeight
    - w.holes * m.holes
    - w.bumps * m.bumps
    + w.lines * m.cleared * m.cleared
    - w.trim * Math.abs(m.list)
    - w.wells * m.deepestWell
  );
}

/**
 * The bot's chosen placement, or null if the game is over / nothing is legal.
 *
 * `rng` + `epsilon` exist because two identical bots on an identical seed play
 * an identical game, and this game's multiplayer is a same-seed parallel race —
 * so a 2-player sim was 100% DRAWS on the first run, measuring nothing at all.
 * Giving each seat its own rng stream (same epsilon for every seat, so seats
 * stay statistically exchangeable) models the fact that two humans diverge,
 * without introducing a seat-dependent difference the fairness test would then
 * be measuring instead of the game.
 */
export function chooseMove(
  g: Game,
  w: BotWeights,
  rng?: () => number,
  epsilon = 0,
): { x: number; rot: number } | null {
  const cands = g.candidates();
  if (cands.length === 0) return null;
  const scored = cands.map((c) => ({
    mv: { x: c.x, rot: c.rot },
    s: scoreMetrics(evaluateLanding(g, c.land.type, c.land.rot, c.land.x, c.land.y), w),
  }));
  scored.sort((a, b) => b.s - a.s);
  if (rng && epsilon > 0 && scored.length > 1 && rng() < epsilon) {
    const top = Math.min(3, scored.length);
    return scored[Math.floor(rng() * top)].mv;
  }
  return scored[0].mv;
}

/** Play one piece. Returns false when the bot has nothing legal left. */
export function botPlay(g: Game, w: BotWeights, rng?: () => number, epsilon = 0): boolean {
  const mv = chooseMove(g, w, rng, epsilon);
  if (!mv) return false;
  return g.hardDropFrom(mv.x, mv.rot);
}
